// Card and tile artwork served from the app's own /public/assets folder.
const BASE = `${process.env.PUBLIC_URL || ""}/assets`;

const CARD_FILES = {
  house_cube: "card_house_cube",
  urban_park: "card_urban_park",
  urban_forest: "card_urban_forest",
  inland_wetland: "card_inland_wetland",
  mangrove_forest: "card_mangrove_forest",
  green_roof: "card_green_roof",
  green_street: "card_green_street",
  low_sea_wall: "card_low_sea_wall",
  // Action cards.
  sabotage: "card_sabotage",
  relocated: "card_relocate",
  board_thief: "card_board_thief",
  protection_card: "card_protection_card",
  repair_card: "card_repair_card",
};

// v3 step 4: the weather deck (6 faces) and the global event deck (24 cards, file name
// card_global_<NN>_<key>, the number and key come from the server's card).
export const WEATHER_FILES = {
  clear_skies: "card_weather_clear_skies",
  light_rain: "card_weather_light_rain",
  heavy_rain: "card_weather_heavy_rain",
  heat_wave: "card_weather_heat_wave",
  heat_storm: "card_weather_heat_storm",
  storm_surge: "card_weather_storm_surge",
};
export const globalCardFile = (card) => (card?.no && card?.key ? `card_global_${String(card.no).padStart(2, "0")}_${card.key}` : null);

// v3 step 5: the Climate Bank project cards (450x720 WebP, new file names so no ART_VERSION bump) and their back.
export const BANK_FILES = {
  green_roofs: "card_bank_green_roofs_for_all",
  living_coastline: "card_bank_living_coastline",
  clean_energy: "card_bank_clean_energy_transition",
};
export const bankCardSrc = (project) => (BANK_FILES[project] ? `${BASE}/cards/${BANK_FILES[project]}.webp` : null);

const TILE_FILES = {
  urban_park: "tile_urban_park",
  urban_forest: "tile_urban_forest",
  inland_wetland: "tile_inland_wetland",
  mangrove_forest: "tile_mangrove_forest",
};

// Card frame = the new 900x1440 art.
export const CARD_ART_W = 900;
export const CARD_ART_H = 1440;
export const CARD_RATIO = CARD_ART_H / CARD_ART_W;
export const CARD_ASPECT = `${CARD_ART_W} / ${CARD_ART_H}`;

// Older 900x1620 art shown fitted inside the frame (contain, centred). Empty: every card
// (build + action) is now 900x1440 and fills the frame.
const LEGACY_FILES = new Set([]);
export const isLegacyArt = (key) => LEGACY_FILES.has(CARD_FILES[key]);

// Action card art (900x1440): coloured band (YOUR TURN / RESPONSE + cost coin) y 35-145,
// illustration y 175-830, both x 35-864.
const ACTION_KEYS = new Set(["sabotage", "relocated", "board_thief", "protection_card", "repair_card"]);

// Square thumbnail window (source px) used by ArtCrop.
// Action: 830 square from (35,17) = band with cost coin + the whole illustration, no stretching.
// Build: full-bleed art 900x750; the 750 square from x=135 keeps the cost badge (x 740-869) in view.
// Legacy: bordered artwork square (34,34)-(866,866).
export const artWindow = (key) => {
  if (isLegacyArt(key)) return { x: 34, y: 34, size: 832 };
  if (ACTION_KEYS.has(key)) return { x: 35, y: 17, size: 830 };
  return { x: 135, y: 0, size: 750 };
};

// Portrait thumbnail window (source px, 15:24 like the card) for the tiny held action cards on the
// player panel: the band with the cost coin + the right-hand part of the illustration
// (x 340-859, y 17-847). null = show the whole card (contain).
export const artPortraitWindow = (key) => (ACTION_KEYS.has(key) ? { x: 340, y: 17, w: 519, h: 830 } : null);

// Bump every time the card/tile images are re-downloaded (cache-busting).
const ART_VERSION = 9;

// Names printed on the current card art (the big preview). Mini cards (hand + market) show these
// so they match the artwork; cards not listed here use their game name.
const PRINTED_NAMES = {
  house_cube: "House Cube",
  urban_park: "Urban Park",
  urban_forest: "Urban Forest",
  mangrove_forest: "Mangrove Forest",
  green_street: "Green Street",
  low_sea_wall: "Sea Wall",
};
export const printedName = (card) => PRINTED_NAMES[card?.key] || card?.name || "";

export const cardImageSrc = (key) =>
  CARD_FILES[key] ? `${BASE}/cards/${CARD_FILES[key]}.png?v=${ART_VERSION}` : null;

export const weatherCardSrc = (face) => {
  const key = face === "extreme_heat_wave" ? "heat_storm" : face === "sunny_day" ? "clear_skies" : face;
  return WEATHER_FILES[key] ? `${BASE}/cards/${WEATHER_FILES[key]}.webp?v=${ART_VERSION}` : null;
};

export const globalCardSrc = (card) => {
  const file = globalCardFile(card);
  return file ? `${BASE}/cards/${file}.webp?v=${ART_VERSION}` : null;
};

// The weather and global backs and all weather / global faces are 450x720 WebP; the build and
// action backs are still PNG.
const WEBP_BACKS = new Set(["weather", "global"]);
export const cardBackSrc = (kind) =>
  `${BASE}/cards/card_back_${kind}.${WEBP_BACKS.has(kind) ? "webp" : "png"}?v=${ART_VERSION}`;

// Warm the browser cache so the weather overlay never shows a card popping in.
const preloaded = new Set();
export function preloadImage(src) {
  if (!src || preloaded.has(src) || typeof Image === "undefined") return;
  preloaded.add(src);
  const img = new Image();
  img.decoding = "async";
  img.src = src;
}
// When the main game starts: the 6 weather faces + the weather and global backs.
export function preloadWeatherArt() {
  Object.keys(WEATHER_FILES).forEach((k) => preloadImage(weatherCardSrc(k)));
  preloadImage(cardBackSrc("weather"));
  preloadImage(cardBackSrc("global"));
}

export const tileImageSrc = (key) =>
  TILE_FILES[key] ? `${BASE}/tiles/${TILE_FILES[key]}.png?v=${ART_VERSION}` : null;

const logged = new Set();
export function logMissingArt(file) {
  const name = String(file).split("/").pop();
  if (logged.has(name)) return;
  logged.add(name);
  console.warn(`[Porosity] Missing art file: ${name} — showing the old design`);
}
